import { useState } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { showToaster } from "../../../common/common";
import PostRequest from "../../../services/PostRequest";

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [pane, setPane] = useState("form");
  const [sentToEmail, setSentToEmail] = useState("");
  const [serverError, setServerError] = useState("");

  const handleForgotEmailPass = async (data) => {
    setServerError("");
    try {
      const options = {
        showLoader: "fp-btn",
        auth: false,
      };
      const response = await PostRequest("/users/otp/send", data, options);
      setSentToEmail(data.email);
      setPane("done");
      navigate("/password/reset", { state: response?.user });
    } catch (error) {
      // Persistent inline message (the toast is brief and easy to miss) so the
      // user clearly sees e.g. "No account found with that email address."
      setServerError(error.message || "Something went wrong. Please try again.");
      showToaster(error.message, "error");
    }
  };

  const validate = (values) => {
    const errors = {};
    if (!values.email) {
      errors.email = "Email Required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = "Invalid email address";
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validate,
    onSubmit: (data) => handleForgotEmailPass(data),
  });

  const handleShowForm = () => {
    setPane("form");
    formik.resetForm();
  };

  return (
    <div className="auth" data-screen-label="Forgot Password">
      <div className="auth__main">
        <div className="auth__card">
          <img
            className="auth__logo auth__logo--color"
            src="/assets/brand/multicartapi-logo.png"
            alt="MultiCartAPI"
          />
          <img
            className="auth__logo auth__logo--white"
            src="/assets/brand/multicartapi-logo-white.png"
            alt="MultiCartAPI"
          />

          {pane === "form" && (
            <>
              <h1>Forgot password?</h1>
              <p className="auth__sub">
                Enter your account email and we'll send you a code to reset your
                password.
              </p>
              <form className="auth__form" onSubmit={formik.handleSubmit}>
                <div className="mca-field">
                  <label className="mca-label" htmlFor="fp-email">
                    Email <span className="req">*</span>
                  </label>
                  <div className="mca-input-group">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="5" width="18" height="14" rx="2" />
                      <path d="m3 7 9 6 9-6" />
                    </svg>
                    <input
                      className="mca-input"
                      id="fp-email"
                      type="email"
                      placeholder="you@company.com"
                      name="email"
                      value={formik.values.email}
                      onChange={(e) => {
                        formik.handleChange(e);
                        if (serverError) setServerError("");
                      }}
                      onBlur={formik.handleBlur}
                      required
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <span className="mca-help" style={{ color: "var(--mca-danger)" }}>
                      {formik.errors.email}
                    </span>
                  )}
                </div>
                {serverError && (
                  <div
                    className="mca-help"
                    style={{
                      color: "var(--mca-danger)",
                      display: "block",
                      marginBottom: "12px",
                    }}
                  >
                    {serverError}{" "}
                    <a href="/sign-up">Create an account?</a>
                  </div>
                )}
                <button
                  className="mca-btn mca-btn--primary"
                  id="fp-btn"
                  type="submit"
                  style={{ width: "100%", marginTop: "4px" }}
                >
                  <span className="mca-btn__spin" hidden>
                    <span className="mca-spinner"></span>
                  </span>
                  <span className="mca-btn__label">Send reset code</span>
                </button>
              </form>
            </>
          )}

          {pane === "done" && (
            <div style={{ textAlign: "center" }}>
              <div
                className="mca-empty__icon"
                style={{
                  background: "var(--mca-success-subtle)",
                  color: "var(--mca-success)",
                  marginBottom: "18px",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 6 9-6" />
                  <path d="m16 13 2 2 4-4" />
                </svg>
              </div>
              <h1>Check your email</h1>
              <p className="auth__sub">
                We sent a reset code to <strong>{sentToEmail}</strong>. It
                expires in 10 minutes.
              </p>
              <a
                className="mca-btn mca-btn--primary"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/password/reset", { state: undefined });
                }}
                style={{ width: "100%", display: "inline-block", marginTop: "16px" }}
              >
                Enter reset code
              </a>
              <div className="auth__foot">
                Didn't receive it?{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleShowForm();
                  }}
                >
                  Try another email
                </a>
              </div>
            </div>
          )}

          <a
            className="auth__back"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/sign-in");
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 6-6 6 6 6" />
            </svg>
            Back to sign in
          </a>
        </div>
      </div>
      <div className="auth__brand">
        <div>
          <img
            className="auth__brand-logo"
            src="/assets/brand/multicartapi-logo-white.png"
            alt="MultiCartAPI"
          />
          <h2>Locked out? No problem.</h2>
          <p>
            Reset your password in two steps and get straight back to your data.
          </p>
        </div>
        <ul className="auth__feat">
          <li>
            <div className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <span>Secure code-based reset</span>
          </li>
          <li>
            <div className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <span>Codes expire in 10 minutes</span>
          </li>
          <li>
            <div className="ck">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <span>Your API key stays unchanged</span>
          </li>
        </ul>
        <div className="auth__brand-foot">
          All your data is encrypted end to end and protected by industry standards.
        </div>
      </div>
    </div>
  );
};
